import React, { useEffect, useState } from 'react';
import { Stack, Chip, Button, Typography, Box } from '@mui/material';

/**
 * Selection state for a set of series keys (years, channels…). Defaults to all
 * on; resets to all when the available keys change (e.g. data loads).
 */
export function useToggleSet(keys) {
  const keyStr = keys.join('|');
  const [selected, setSelected] = useState(() => new Set(keys));
  useEffect(() => {
    setSelected(new Set(keys));
  }, [keyStr]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (k) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  return {
    selected,
    toggle,
    all: () => setSelected(new Set(keys)),
    none: () => setSelected(new Set()),
  };
}

/**
 * A row of toggle chips with a colour swatch per item, plus All / None.
 * Props: { label?, items:[{key,label,colour}], selected:Set, onToggle, onAll, onNone }
 */
export default function SeriesToggle({ label, items, selected, onToggle, onAll, onNone }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{label}</Typography>
      )}
      {items.map((it) => {
        const on = selected.has(it.key);
        return (
          <Chip
            key={it.key}
            size="small"
            label={it.label}
            onClick={() => onToggle(it.key)}
            variant={on ? 'filled' : 'outlined'}
            icon={
              <Box
                component="span"
                sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: it.colour, ml: 0.75, opacity: on ? 1 : 0.4 }}
              />
            }
            sx={{ opacity: on ? 1 : 0.5 }}
          />
        );
      })}
      <Button size="small" onClick={onAll}>All</Button>
      <Button size="small" onClick={onNone}>None</Button>
    </Stack>
  );
}
